<template>
  <TOverlay :show="isLoading">
    <div
      v-bind="$attrs"
      class="overflow-x-auto overflow-y-hidden scrollbar-hidden @container"
    >
      <div class="flex @md:grid @md:grid-cols-2 gap-2 items-center @lg:grid-cols-5">
        <button
          v-for="({ item, retries, error, loaded }, index) in itemMap"
          :key="item.id"
          type="button"
          class="w-full rounded-md bg-gray-100 min-w-fit aspect-3/2"
          @click="handleClick(index)"
        >
          <ClientOnly>
            <img
              v-if="!error"
              :ref="el => imageRefs[index] = (el as HTMLImageElement)"
              :key="`${item.id}-${retries}`"
              :src="`${item.thumbnailUrl}`"
              :alt="item?.name"
              :class="{ hidden: !loaded }"
              class="w-full h-full object-cover rounded-md"
              @error="handleImageError(index)"
              @load="handleImageLoaded(index)"
            >
            <TIcon
              v-else
              :icon="['far', 'image-slash']"
            />
          </ClientOnly>
        </button>
      </div>
    </div>
  </TOverlay>

  <ClientOnly>
    <FsLightbox
      :toggler="toggler"
      :sources="lightBoxImages"
      :thumbs="lightBoxThumbsImages"
      :slide="lightBoxImageIndex"
      :show-thumbs-on-mount="lightBoxImages.length > 1"
      type="image"
    />
  </ClientOnly>
</template>

<script setup lang="ts">
import type { Nullable } from '~/types'
import FsLightbox from '~/vendor/fslightbox-vue/v3'
import type { GalleryItem } from './types'

const props = defineProps<{
  items: GalleryItem[]
}>()

defineOptions({
  inheritAttrs: false,
})

const { getDynamicImage, presets } = useImage()

const MAX_FILE_RETRIES = 5
const IMAGE_RETRY_TIMEOUT = 2000

type FileMapEntry = {
  item: GalleryItem
  loaded: boolean
  retries: number
  timeout: Nullable<ReturnType<typeof setTimeout>>
  error: boolean
}

const imageRefs = ref<HTMLImageElement[]>([])

const itemMap: Ref<FileMapEntry[]> = ref(props.items.map((item) => ({
  item,
  loaded: false,
  retries: 0,
  timeout: null,
  error: false,
})))

watch(() => props.items, (newItems, oldItems) => {
  // Clear all timeouts.
  itemMap.value.forEach(({ timeout }) => {
    if (timeout) {
      clearTimeout(timeout)
    }
  })

  // Update the item map.
  itemMap.value = newItems.map((item, index) => ({
    item,
    loaded: index < oldItems.length,
    retries: 0,
    timeout: null,
    error: false,
  }))
})

const isLoading = computed(() => itemMap.value.some(({ loaded }) => !loaded))

function handleClick(index: number) {
  showLightBox(index)
}

const lightBoxImageIndex = ref<Nullable<number>>(null)
const lightBoxImages = computed(() =>
  props.items.map((item) =>
    getDynamicImage({ path: item.path, preset: presets.NATIVE_LG }) ?? item.imageUrl,
  ),
)
const lightBoxThumbsImages = computed(() => props.items.map(({ thumbnailUrl }) => thumbnailUrl))

const toggler = ref(false)

function showLightBox(index: number) {
  lightBoxImageIndex.value = index + 1
  toggler.value = !toggler.value
}

function handleImageError(index: number) {
  const item = itemMap.value[index]

  if (!item) {
    return
  }

  if ((item.retries ?? 0) >= MAX_FILE_RETRIES) {
    item.error = true
    item.loaded = true
  }
  else {
    item.timeout = setTimeout(() => {
      item.retries++
    }, IMAGE_RETRY_TIMEOUT)
  }

  itemMap.value[index] = item
}

function handleImageLoaded(index: number) {
  const item = itemMap.value?.[index]

  if (!item) {
    return
  }

  item.loaded = true

  itemMap.value[index] = item
}

onBeforeUnmount(() => {
  itemMap.value.forEach(({ timeout }) => {
    if (timeout) {
      clearTimeout(timeout)
    }
  })
})
</script>
